import { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Count, Progress } from '@elfsight/lib-toolkit-slider';
import {
  getColorWithAlpha,
  getHandleActionKeyDown
} from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import classNames from 'classnames';
import { clamp } from 'es-toolkit';
import { APP_CLASSNAME_PREFIX } from '../../../constants';

const THUMBNAIL_WIDTH = 72;
const PROGRESS_LINE_HEIGHT = 2;

export const Thumbnails = ({
  colors,
  images,
  currentIndex,
  slides,
  goTo,
  autoplayProgressBar,
  autoplayPauseOnHover,
  autoplayEnabled,
  seconds,
  loop,
  isNarrow
}) => {
  const { placeholderImage, arrowsPosition } = useSettings();
  const { color, active } = colors;

  const shouldReduceThumbnails =
    isNarrow && arrowsPosition.startsWith('bottom');

  const thumbnailsCount = shouldReduceThumbnails ? 2 : 3;
  const paginationWindowWidth = thumbnailsCount * THUMBNAIL_WIDTH;

  const trackThumbnailIndex = shouldReduceThumbnails ? 0 : 1;

  const maxInsetMultiplier = clamp(
    images.length - thumbnailsCount,
    0,
    images.length - 1
  );

  const currentInsetMultiplier = clamp(
    currentIndex - trackThumbnailIndex,
    0,
    maxInsetMultiplier
  );

  const currentLeftInset = -currentInsetMultiplier * THUMBNAIL_WIDTH;

  const items = useMemo(
    () =>
      images.map((item, index) => {
        const isActive = index === currentIndex;

        return (
          <Item
            key={index}
            role="button"
            tabIndex={index === currentIndex ? -1 : 0}
            aria-label={`Slide ${index + 1} of ${slides.length}`}
            onClick={() => goTo(index)}
            onKeyDown={getHandleActionKeyDown(() => goTo(index))}
            $isActive={isActive}
            $left={currentLeftInset}
          >
            <Image $url={item || placeholderImage} />

            {autoplayProgressBar && autoplayEnabled && (
              <ProgressWrapper
                $bgColor={getColorWithAlpha(color, 0.5)}
                $activeColor={active}
              >
                <Count
                  seconds={seconds}
                  autoplayProgressBar={autoplayProgressBar}
                  autoplayPauseOnHover={autoplayPauseOnHover}
                  active={isActive}
                  last={slides.length - 1 === currentIndex && !loop}
                  index={index}
                >
                  {({ percent }) => (
                    <Progress.Line
                      percent={percent}
                      height={PROGRESS_LINE_HEIGHT}
                      appClassnamePrefix={APP_CLASSNAME_PREFIX}
                    />
                  )}
                </Count>
              </ProgressWrapper>
            )}
          </Item>
        );
      }),
    // oxlint-disable-next-line exhaustive-deps
    [
      images,
      currentIndex,
      autoplayProgressBar,
      autoplayEnabled,
      seconds,
      autoplayPauseOnHover,
      slides,
      loop,
      placeholderImage
    ]
  );

  const width =
    slides.length <= 3
      ? slides.length * THUMBNAIL_WIDTH
      : paginationWindowWidth;

  return (
    <Container $isNarrow={isNarrow}>
      <Inner $width={width} $isCentered={slides.length < 3}>
        {items}
      </Inner>
    </Container>
  );
};

const Container = styled.div.attrs({
  className: `${APP_CLASSNAME_PREFIX}-thumbnails-component`
})`
  display: flex;
  overflow: hidden;
  width: auto;
  position: static;
  z-index: 1;
  max-width: 100%;
  padding: 16px;
  height: 88px;

  ${({ $isNarrow }) =>
    $isNarrow &&
    css`
      padding-left: 0;
      padding-right: 0;
    `}
`;

const Inner = styled.div.attrs({
  className: `${APP_CLASSNAME_PREFIX}-thumbnails-inner`
})`
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: 4px;

  width: ${({ $width }) => $width}px;
  ${({ $isCentered }) => $isCentered && 'justify-content: center;'}
`;

const Image = styled.div.attrs({
  className: `${APP_CLASSNAME_PREFIX}-thumbnails-image`
})`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-image: url(${({ $url }) => $url});
`;

const ProgressWrapper = styled.div.attrs({
  className: `${APP_CLASSNAME_PREFIX}-thumbnails-progress`
})`
  width: calc(100% - 8px);
  position: absolute;
  left: 4px;
  bottom: 3px;
  height: 2px;
  border-radius: 2px;

  background-color: ${({ $bgColor }) => $bgColor};

  & .${APP_CLASSNAME_PREFIX}-progress-line {
    background-color: ${({ $activeColor }) => $activeColor};
    height: 2px;
  }
`;

const Item = styled.div.attrs(({ $isActive }) => ({
  className: classNames(`${APP_CLASSNAME_PREFIX}-thumbnails-item`, {
    [`${APP_CLASSNAME_PREFIX}-thumbnails-active`]: $isActive
  })
}))`
  width: 56px;
  height: 36px;
  opacity: 0.7;
  margin: 0 8px;
  transition: 0.3s ease all;
  flex-shrink: 0;
  cursor: pointer;
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);

  left: ${({ $left }) => $left}px;

  &:after {
    content: '';
    position: absolute;
    inset: 0;
    border: 1px solid rgba(17, 17, 17, 0.1);
    border-radius: 4px;
  }

  ${({ $isActive }) => `
    ${
      $isActive
        ? css`
            opacity: 1;
            width: 72px;
            height: 48px;
            margin: 0;
          `
        : css`
            &:hover {
              width: 60px;
              height: 40px;
              margin: 0 6px;
              opacity: 1;
            }
          `
    }
  `}
`;
